import { Theme, useMediaQuery } from '@mui/material';
import { concat } from 'lodash';
import React, { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { TableInstance } from 'react-table';

import { DocumentPreviewContainer } from '@work4all/components/lib/components/entity-preview/document-preview';
import { useTableStateBag } from '@work4all/components/lib/dataDisplay/basic-table';

import { useDataProvider, useUser } from '@work4all/data';
import { DOCUMENT_FILTER_EXCLUDE_CHILD_DOCUMENTS } from '@work4all/data/lib/prefilters/document';

import { Document } from '@work4all/models/lib/Classes/Document.entity';
import { DataRequest, SortDirection } from '@work4all/models/lib/DataProvider';
import { EMode } from '@work4all/models/lib/Enums/EMode.enum';
import { Entities } from '@work4all/models/lib/Enums/Entities.enum';

import { Table } from '../../components/data-tables/table/Table';
import { OnOpenMask } from '../../containers/mask-overlays/mask-overlay';
import { useDocumentPdfEditRights } from '../../containers/mask-overlays/mask-overlay/views/pdf-editor/use-document-pdf-edit-rights';
import { usePdfEditorOverlayMask } from '../../containers/mask-overlays/mask-overlay/views/pdf-editor/use-pdf-editor-overlay-mask';
import { PersonalAndGeneralView } from '../filters/PersonalAndGeneralView';

import { PreviewMobileWrapper } from './PreviewMobileWrapper';
import schema from './schemata/document-table-schema.json';
import { TableLayoutContext, useTableLayoutState } from './table-layout';
import { TableNoRowsPlaceholder } from './TableNoRowsPlaceholder';
import { useDataTable } from './use-data-table';
import { useDeleteEntitiesToolbarConfig } from './use-delete-entities-toolbar-config';
import { useTableMaskHandlers } from './use-table-mask-handlers';
import { useUserColumnConfigs } from './use-user-column-configs';

interface Props {
  prefilter?: unknown[];
  onOpenMask: OnOpenMask;
}

const defaultSort = [{ field: 'date', direction: SortDirection.DESCENDING }];
const manualGroupBy = true;

export const DocumentsTable = React.forwardRef<TableInstance, Props>(
  function DocumentsTable(props, ref) {
    const { prefilter: prefilterProp, onOpenMask } = props;
    const { entity: entityType } = schema as never;

    const prefilter = useMemo(() => {
      return concat(
        ...[prefilterProp, DOCUMENT_FILTER_EXCLUDE_CHILD_DOCUMENTS].filter(
          Boolean
        )
      );
    }, [prefilterProp]);

    const tableStateBag = useTableStateBag();

    const layoutState = useTableLayoutState();
    const [layout] = layoutState;

    const {
      columnConfigs,
      cardConfig,
      prepareRowDisplayModifiers,
      data,
      fetchMore,
      total,
      selectedEntity,
      initialSortBy,
      pending,
    } = useDataTable<Document, never>({
      layout,
      schema: schema as never,
      tableStateBag,
      prefilter,
      defaultSort,
    });

    const maskHandlers = useTableMaskHandlers(entityType, onOpenMask);

    const deleteConfig = useDeleteEntitiesToolbarConfig({
      entityType,
    });

    const [userConfig, userConfigMethods] = useUserColumnConfigs({
      layout,
      entityType,
      columnConfigs,
    });

    const isDesktop = useMediaQuery<Theme>((theme) =>
      theme.breakpoints.up('sm')
    );

    const { t } = useTranslation();
    const user = useUser();

    const previewRequest = useMemo<DataRequest>(() => {
      const data: Document<EMode.query> = {
        id: null,
        userId: null,
        fileInfos: {
          fileServiceProviderInfos: {
            id: null,
            fspUrl: null,
            fileName: null,
          },
        },
      };

      return {
        entity: Entities.document,
        data,
        completeDataResponse: false,
        filter: [{ id: { $eq: selectedEntity?.id } }],
      };
    }, [selectedEntity?.id]);

    const selectedEntityResult = useDataProvider<Document>(previewRequest);

    const { data: selectedEntityData, refetch } = selectedEntityResult;

    const selectedDocument = selectedEntityData?.[0];

    const pdfEditAllowed = useDocumentPdfEditRights({
      fileName: selectedDocument?.fileInfos?.fileServiceProviderInfos?.fileName,
      document: selectedDocument,
      userRights: user.benutzerRechte,
      userId: user.benutzerCode,
    });

    const { pdfEditOverlay, open: openPdfEditOverlay } =
      usePdfEditorOverlayMask({
        entityId: selectedEntity?.id,
        entityType: Entities.document,
        docId: selectedDocument?.id,
        onClose: refetch,
      });

    const handleOnDoubleClick = useCallback(() => {
      const selectedEntity = selectedEntityResult?.[0];
      if (selectedEntity?.fileInfos?.fileServiceProviderInfos?.fspUrl) {
        window.open(
          selectedEntity?.fileInfos?.fileServiceProviderInfos?.fspUrl,
          '_blank'
        );
      } else {
        maskHandlers.edit.handler(selectedEntity?.id);
      }
    }, [maskHandlers.edit, selectedEntityResult]);

    if (!userConfig) return null;

    return (
      <>
        {pdfEditOverlay}
        <TableLayoutContext value={layoutState}>
          <Table
            pending={pending}
            noRowsRenderer={() => <TableNoRowsPlaceholder />}
            ref={ref}
            layout={layout}
            cardConfig={cardConfig}
            areas={{
              left: {
                content: <PersonalAndGeneralView entity={Entities.document} />,
                resizable: true,
                collapseConfig: {
                  title: t('COMMON.FILTER'),
                },
              },
              right: selectedEntity !== null && {
                content: (
                  <PreviewMobileWrapper
                    active={!isDesktop}
                    actions={{
                      edit: maskHandlers.edit,
                      convert: {
                        exclude: entityType,
                        handler: maskHandlers.convert,
                      },
                      remove: deleteConfig,
                    }}
                  >
                    <DocumentPreviewContainer
                      documentId={selectedEntity.id}
                      onCloseClicked={() =>
                        tableStateBag.tableInstance.toggleAllRowsSelected(false)
                      }
                      onEditClicked={maskHandlers.edit.handler}
                      onShareClicked={maskHandlers.share}
                      onEditPdfClicked={
                        pdfEditAllowed
                          ? () => {
                              openPdfEditOverlay();
                            }
                          : undefined
                      }
                    />
                  </PreviewMobileWrapper>
                ),
              },
            }}
            columnConfigs={userConfig}
            manualGroupBy={manualGroupBy}
            initialSortBy={initialSortBy}
            loadMoreItems={fetchMore}
            onRowDoubleClick={handleOnDoubleClick}
            actions={{
              add: maskHandlers.create,
              edit: maskHandlers.edit,
              convert: {
                exclude: entityType,
                handler: maskHandlers.convert,
              },
              remove: deleteConfig,
              resetColumns: userConfigMethods.remove,
              createWidget: true,
            }}
            prepareRowDisplayModifiers={prepareRowDisplayModifiers}
            data={data}
            total={total}
          />
        </TableLayoutContext>
      </>
    );
  }
);
